import { DateTime } from '@ksyos/date-time';
import {
    Alert,
    Badge,
    Card,
    IconButton,
    Link,
    PageHeader,
    PageLayout,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IEmploymentTaxesDetailsExport } from '@common/interfaces/api/employmentTaxesDetailsExportApi';
import { EmploymentTaxesDetailsExportStatus } from '@common/enums/employmentTaxesDetailsExportStatus';
import { ManagerAuthorization } from '@common/enums/managerAuthorization';
import {
    getAllEmploymentTaxesDetailsExports,
    rescheduleEmploymentTaxesDetailsExport,
} from '@frontend/services/api/employmentTaxesDetailsExportApiService';
import { getSelectedLanguage } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils/assertHasAuthorization';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { RequestEmploymentTaxesDetailsExportModal } from './RequestEmploymentTaxesDetailsExportModal';

export function EmploymentTaxesDetailsExport() {
    assertHasAuthorization(ManagerAuthorization.exportUbd);

    const language = getSelectedLanguage();

    const abortSignal = useAbortSignal();
    const [employmentTaxesDetailsExports, setEmploymentTaxesDetailsExports] = useState<IEmploymentTaxesDetailsExport[]>(
        []
    );
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [requestedEmploymentTaxesDetailsExport, setRequestedEmploymentTaxesDetailsExport] =
        useState<IEmploymentTaxesDetailsExport | null>(null);

    useEffect(() => {
        fetchEmploymentTaxesDetailsExports();
    }, []);

    return (
        <PageLayout>
            <PageHeader title={language.finance.employmentTaxesDetailsExport.ubd} />
            <Card>
                <Card.Header title={language.finance.exports} />
                <Alert
                    appearance="info"
                    hasIcon={true}
                >
                    {language.finance.employmentTaxesDetailsExport.ubdExportAlert}
                </Alert>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableHeader>
                                    {language.finance.employmentTaxesDetailsExport.declarationYear}
                                </TableHeader>
                                <TableHeader>{language.finance.employmentTaxesDetailsExport.exportDate}</TableHeader>
                                <TableHeader>{language.finance.employmentTaxesDetailsExport.createdBy}</TableHeader>
                                <TableHeader align="center">{language.finance.requestExportAgain}</TableHeader>
                                <TableHeader align="right">{language.common.download}</TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {employmentTaxesDetailsExports.map((employmentTaxesDetails) => (
                                <TableRow key={employmentTaxesDetails.id}>
                                    <TableCell fontWeight="medium">{employmentTaxesDetails.declarationYear}</TableCell>
                                    <TableCell>
                                        {DateTime.from(employmentTaxesDetails.exportDate).format('DD-MM-YYYY')}
                                    </TableCell>
                                    <TableCell>{employmentTaxesDetails.requestedByDisplayName}</TableCell>
                                    <TableCell align="center">
                                        {employmentTaxesDetails.status ===
                                            EmploymentTaxesDetailsExportStatus.scheduled && (
                                            <Badge
                                                icon="waiting"
                                                text={language.finance.employmentTaxesDetailsExport.exportIsRequested}
                                            />
                                        )}
                                        {[
                                            EmploymentTaxesDetailsExportStatus.success,
                                            EmploymentTaxesDetailsExportStatus.failed,
                                        ].includes(employmentTaxesDetails.status) && (
                                            <IconButton
                                                icon="refresh"
                                                onClick={() => handleOpenModal(employmentTaxesDetails)}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {employmentTaxesDetails.status === EmploymentTaxesDetailsExportStatus.success &&
                                            employmentTaxesDetails.downloadUrl && (
                                                <Link
                                                    color="minimal"
                                                    href={employmentTaxesDetails.downloadUrl}
                                                    iconLeft="download"
                                                    openInNewTab={true}
                                                    variant="externalLink"
                                                />
                                            )}
                                        {employmentTaxesDetails.status ===
                                            EmploymentTaxesDetailsExportStatus.failed && (
                                            <Badge text={language.finance.employmentTaxesDetailsExport.exportFailed} />
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <RequestEmploymentTaxesDetailsExportModal
                    declarationYear={requestedEmploymentTaxesDetailsExport?.declarationYear ?? language.common.unknown}
                    isOpen={isModalOpen}
                    onCancel={handleCloseModal}
                    onClose={handleCloseModal}
                    onRequest={handleRescheduleExport}
                />
            </Card>
        </PageLayout>
    );

    function handleCloseModal() {
        setIsModalOpen(false);
        setRequestedEmploymentTaxesDetailsExport(null);
    }

    function handleOpenModal(nextRequestedEmploymentTaxesDetailsExport: IEmploymentTaxesDetailsExport) {
        setRequestedEmploymentTaxesDetailsExport(nextRequestedEmploymentTaxesDetailsExport);
        setIsModalOpen(true);
    }

    async function handleRescheduleExport() {
        if (requestedEmploymentTaxesDetailsExport) {
            await rescheduleEmploymentTaxesDetailsExport(
                {
                    employmentTaxesDetailsExportId: requestedEmploymentTaxesDetailsExport.id,
                },
                abortSignal
            );

            await fetchEmploymentTaxesDetailsExports();

            handleCloseModal();
        }
    }

    async function fetchEmploymentTaxesDetailsExports() {
        const nextEmploymentTaxesDetailsExports = await getAllEmploymentTaxesDetailsExports(abortSignal);

        setEmploymentTaxesDetailsExports(nextEmploymentTaxesDetailsExports.items);
    }
}
